import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const ProjectTeamManager = ({ project, onClose }) => {
  const { users, assignEmployeeToProject, removeEmployeeFromProject } = useData();
  const { currentUser } = useAuth();
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [loading, setLoading] = useState(false);

  // Only owners can manage teams
  if (currentUser?.role !== 'owner') {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>Access Denied</h3>
            <button onClick={onClose} className="modal-close">×</button>
          </div>
          <div className="modal-body">
            <p className="text-primary">Only owners can manage project teams.</p>
          </div>
        </div>
      </div>
    );
  }

  const teamMembers = users.filter(user => 
    project.teamMembers && project.teamMembers.includes(user.id)
  );

  const availableEmployees = users.filter(user => 
    (user.role === 'employee' || user.role === 'project_manager') &&
    (!project.teamMembers || !project.teamMembers.includes(user.id))
  );

  const handleAssignEmployee = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    
    setLoading(true);
    try {
      await assignEmployeeToProject(project.id, selectedEmployee);
      setSelectedEmployee('');
    } catch (error) {
      console.error('Error assigning employee:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEmployee = async (employeeId) => {
    setLoading(true);
    try {
      await removeEmployeeFromProject(project.id, employeeId);
    } catch (error) {
      console.error('Error removing employee:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Manage Team - {project.name}</h3>
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="modal-body">
          {/* Add Employee Form */}
          <div className="form-section">
            <h4 className="text-primary">Add Team Member</h4>
            <form onSubmit={handleAssignEmployee}>
              <div className="form-row">
                <div className="form-group" style={{flex: 1}}>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="form-control"
                    required
                  >
                    <option value="">Select employee to add...</option>
                    {availableEmployees.map(employee => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name} - {employee.role.replace('_', ' ')} ({employee.department})
                      </option>
                    ))}
                  </select>
                </div>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !selectedEmployee}
                >
                  <i className="fas fa-plus"></i>
                  Add
                </button>
              </div>
            </form>
          </div>

          {/* Current Team Members */}
          <div className="form-section">
            <h4 className="text-primary">Current Team Members ({teamMembers.length})</h4>
            {teamMembers.length > 0 ? (
              <div className="team-list">
                {teamMembers.map(member => (
                  <div key={member.id} className="team-member-item">
                    <div className="member-info">
                      <div className="member-name text-primary">{member.name}</div>
                      <div className="member-role text-secondary">
                        {member.role.replace('_', ' ')} - {member.department}
                      </div>
                      <div className="member-email text-muted">{member.email}</div>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemoveEmployee(member.id)}
                      disabled={loading}
                    >
                      <i className="fas fa-times"></i>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                <i className="fas fa-users"></i>
                <p className="text-secondary">No team members assigned yet</p>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button 
              type="button" 
              onClick={onClose}
              className="btn btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTeamManager;
